import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, IconButton, Modal, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HeadTitle from '../../components/HeadTitle';
import Loader from '../../components/common/Loader/Loader';
import UserProfileMainPage from '../../components/user-profile-main/UserProfileMainPage';
import { getBotCoverage } from '../../api/botCoverage';
import styles from './styles';
import { SAMPLE_MEMBERS, SAMPLE_CITY_GAPS, SAMPLE_TOTALS } from './sampleData';
import { STATUS_DESCRIPTION, STATUS_LABEL, statusLabel } from '../../utils/memberStatus';

/**
 * Bot Coverage — the members no bot is reaching, and why.
 *
 * Native render (no iframe) scoped under `.favor-bc`, mirroring Messaging
 * Activity. Everything — filtering, sorting, paging, totals — is computed
 * server-side over the whole filtered population, so the tiles describe the
 * population and never the page slice on screen.
 *
 * THE THREE SCOPES ARE DISJOINT and each has a different fix, which is the whole
 * point of separating them:
 *   No bot in range     a bot exists but none within the radius → place bots there
 *   Never messaged      a bot IS in range and still nobody wrote → send to them
 *   No location         no coordinates on either store → nothing bot-side to fix
 * Every member sits in exactly one, plus a fourth ("covered") the tab doesn't list.
 * Folding "we can't place them" into "no bot is near them" would blame the bot
 * roster for thousands of members whose problem is a missing location.
 *
 * RADIUS IS FIXED at the PRODUCT'S OWN BOT REACH — 150 miles — and is not exposed
 * as a control. That is the cap on both paths by which a bot can reach anyone: the
 * bot message sender's $geoNear (server utils/cronJobs.js:1482,
 * `MAX_DISTANCE = 150 * 1609.34`) and the member's own distance slider (client
 * RangeSlider.js, `max={150}`). Past it nobody reaches anybody, so there is one
 * correct answer and nothing for an admin to choose.
 *
 * There is no PER-MEMBER radius to use instead: `users.preferences.distance` is
 * unset on all 28,781 members, and `user_filter.maxDistance` exists for only 53 of
 * them (saved named filters) — far too few to drive a population report. The live
 * value is whatever the client puts in `where.maxDistance` per request and is
 * never persisted.
 *
 * The number is read off the RESPONSE (`radiusKm` / `botReachMiles`), never
 * hardcoded into a label here, so the page cannot claim a distance the server did
 * not actually bucket by.
 *
 * On any failure we fall back to the mockup sample behind an amber chip so the tab
 * never renders blank — but never silently, because every sample number is invented.
 */
const SCOPES = [
  {
    key: 'norange',
    label: 'No bot in range',
    hint: 'No bot within the radius below. A coverage gap — place bots near them.',
    bucket: 'norange',
  },
  {
    key: 'nomessage',
    label: 'Never messaged',
    hint: 'A bot IS in range and they have still never received a bot message.',
    bucket: 'nomessage',
  },
  {
    key: 'nolocation',
    label: 'No location',
    hint: 'No usable coordinates on either store, so no proximity search can place them.',
    bucket: 'nolocation',
  },
];

// `active` = every Active member, approved + hidden (the default). The server also takes
// `approved` for on-search-only, which this tab does not offer.
const AUDIENCES = [['active', 'Active only'], ['all', 'All members']];
const JOINED = [['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['90d', 'Last 90 days'], ['all', 'All time'], ['custom', 'Custom']];
const PLANS = [['All', 'All plans'], ['paid', 'Paying now'], ['Luxury', 'Luxury'], ['Gold', 'Gold'], ['free', 'Free']];
const SORTS = {
  wait: 'Longest wait first',
  distance: 'Furthest from a bot',
  paid: 'Highest paying first',
  newest: 'Newest first',
  active: 'Recently active',
};
/**
 * "In range" is FIXED at the product's own bot reach — 150 miles — and is not a
 * control. Two independent paths cap it at the same 150, in MILES:
 *   server utils/cronJobs.js:1482  MAX_DISTANCE = 150 * 1609.34  (bot message sender)
 *   client RangeSlider.js          <Slider min={1} max={150} />  (member's own search)
 * Past it a member is out of reach of the outbound cron AND of their own search,
 * so there is one correct answer to "is a bot in range" and no reason to ask the
 * admin for a number.
 *
 * The value is READ OFF THE RESPONSE (`botReachMiles` / `radiusKm`) rather than
 * hardcoded here, so the constant lives in exactly one place — the controller that
 * actually did the bucketing. These are only the fallback for sample/placeholder
 * mode, where there is no response to read.
 */
const BOT_REACH_MILES = 150;
const BOT_REACH_KM = 241;
const PAGE_SIZE = 50;

// Matches the profile modal on Member Engagement / Channel Attribution.
const PROFILE_MODAL_SX = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: 1300,
  width: '95%',
  margin: '0 auto',
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: 24,
  p: '0 30px 30px 30px',
  maxHeight: '80vh',
  overflow: 'auto',
};

const iso = (d) => d.toISOString().slice(0, 10);
const TODAY = iso(new Date());
const shiftDays = (isoStr, n) => { const d = new Date(isoStr); d.setDate(d.getDate() + n); return iso(d); };
const fmtClock = (isoStr) => (isoStr ? new Date(isoStr).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const nfmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/**
 * Distances render in MILES, matching the radius control and the two places in the
 * product that actually gate bot reach (cronJobs MAX_DISTANCE, the client's own
 * distance slider). The API carries km — one unit on the wire — so everything
 * converts here, at the last moment.
 *
 * Rounded, never decimal: a nearest-bot figure derived from a city-level
 * coordinate is not meaningful to the metre, and printing it that way would imply
 * a precision the underlying location data does not have.
 */
const KM_TO_MI = 0.621371;
const fmtDist = (km) => {
  if (km == null) return '—';
  const mi = km * KM_TO_MI;
  return mi >= 1000 ? `${(mi / 1000).toFixed(1)}k mi` : `${Math.round(mi)} mi`;
};

/**
 * Location label: the most specific thing we actually HAVE, with whatever is left
 * beneath it. Missing parts are skipped rather than rendered as a dash.
 *
 * `users.city` is an empty string for 68 of the 3,702 active members (1.8%) whose
 * state and country ARE set — a reverse-geocode that returned an administrative
 * area but no locality, verified 2026-08-07. For all 68 there is no city string
 * anywhere else either (`users.location` holds only type+coordinates, and their
 * live-location doc is blank too), so nothing can recover it. Printing "—" implied
 * data loss when "England, United Kingdom" was sitting right there.
 *
 * Deduped case-insensitively because city and state are frequently the same place
 * (Lagos/Lagos, Singapore/Singapore) and "Lagos · Lagos, Nigeria" reads as a bug.
 */
const placeLines = (o = {}) => {
  const seen = new Set();
  const parts = [o.city, o.state, o.country]
    .map((x) => String(x || '').trim())
    .filter((x) => {
      // '—' is the placeholder getBotCoverage groups city-less members under; it
      // is not a place name and must not be promoted over a real state/country.
      if (!x || x === '—' || x === '-') return false;
      const k = x.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  return { head: parts[0] || '—', rest: parts.slice(1).join(', ') };
};

// A city-gap row only drives the City filter when it has a real city: the filter
// matches on the stored value, so clicking the city-less group would send '—' and
// return an empty table.
const hasCity = (c = {}) => !!c.city && c.city !== '—';

const joinedLabel = (d) => (d == null ? '—' : d === 0 ? 'Today' : d === 1 ? 'Yesterday' : `${d} days ago`);
const genderChip = (g) => (g === 'F' ? <span className="pill women">Women</span> : g === 'M' ? <span className="pill men">Men</span> : <span className="pill neutral">Other</span>);
const tierPill = (t) => (t === 'Luxury' ? <span className="pill lux">Luxury</span> : t === 'Gold' ? <span className="pill gold">Gold</span> : <span className="pill free">Free</span>);

// Waiting-time colour, keyed off the mockup's thresholds.
const waitClass = (d) => (d >= 14 ? 'd-bad' : d >= 5 ? 'd-warn' : 'd-ok');
// Severity is relative to the radius in force, not to a fixed number: 160 miles is
// a near miss at the 150-mile default and a chasm at 25. Both arguments are in KM
// (the unit the API speaks); only the rendering is in miles. Green means genuinely
// inside the radius — which is every row on the Never-messaged scope, where a bot
// is right there and still nobody has written.
const distClass = (km, radius) => {
  if (km == null) return 'bad';
  if (km <= radius) return 'ok';
  if (km <= radius * 1.5) return 'warn';
  return 'bad';
};

// The nearest bot ignoring the gender preference is only worth showing when it
// changes the answer: a bot IS within the radius, just not the gender this member
// is seeking. Printing it whenever it is merely a little closer produced rows
// reading "1.8k km · any gender 1.8k km" — the same rounded number twice.
const genderBlocked = (m, radius) =>
  m.nearestAnyKm != null && m.nearestAnyKm <= radius && (m.nearestKm == null || m.nearestKm > radius);

const acctPill = (st) => {
  if (!STATUS_LABEL[st]) return null;
  return <span className={`acct-st ${st}`} title={STATUS_DESCRIPTION[st]}><i />{statusLabel(st)}</span>;
};

const relTime = (isoStr) => {
  if (!isoStr) return '—';
  const mins = Math.floor((Date.now() - new Date(isoStr)) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  const d = Math.floor(mins / 1440);
  return d < 30 ? `${d}d ago` : `${Math.floor(d / 30)}mo ago`;
};

const BotCoverage = ({ embedded = false }) => {
  const [scope, setScope] = useState('norange');
  const [audience, setAudience] = useState('active');
  const [gender, setGender] = useState('all');
  const [plan, setPlan] = useState('All');
  const [country, setCountry] = useState('All');
  const [city, setCity] = useState('All');
  const [joined, setJoined] = useState('all');
  const [from, setFrom] = useState(shiftDays(TODAY, -29));
  const [to, setTo] = useState(TODAY);
  const [sort, setSort] = useState('wait');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [openMenu, setOpenMenu] = useState('');

  const [data, setData] = useState(null);
  const [statusFlag, setStatusFlag] = useState('loading'); // loading | live | placeholder
  const [fetching, setFetching] = useState(true);
  const barRef = useRef(null);

  // No `radius`: the server applies its own DEFAULT_RADIUS_KM (the 150-mile bot
  // reach). Sending one from here would be a second place for that constant to
  // live, and the two could drift.
  const query = useMemo(() => ({
    scope,
    audience,
    sort,
    gender: gender === 'all' ? undefined : gender === 'f' ? 'female' : 'male',
    plan: plan === 'All' ? undefined : plan,
    country: country === 'All' ? undefined : country,
    city: city === 'All' ? undefined : city,
    joined: joined === 'all' ? undefined : joined,
    ...(joined === 'custom' ? { from, to } : {}),
    searchTerm: debouncedQ.trim() || undefined,
  }), [scope, audience, sort, gender, plan, country, city, joined, from, to, debouncedQ]);

  // Any filter change invalidates the current page number. Reset during render
  // rather than in an effect: an effect would run AFTER the fetch effect below had
  // already fired a request pairing the new filters with the stale page.
  const queryKey = JSON.stringify(query);
  const [pageKey, setPageKey] = useState(queryKey);
  const [page, setPage] = useState(1);
  if (queryKey !== pageKey) {
    setPageKey(queryKey);
    setPage(1);
  }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let active = true;
    setFetching(true);
    (async () => {
      try {
        const res = await getBotCoverage({ ...query, page, limit: PAGE_SIZE });
        if (!active) return;
        // `warming` is a legitimate state, not a failure: the snapshot is still
        // being built after a restart. It must NOT drop to sample data — the tab
        // says "building" instead, because invented numbers are worse than none.
        if (res && res.warming) {
          setData(res);
          setStatusFlag('live');
        } else if (res && Array.isArray(res.members) && res.totals) {
          setData(res);
          setStatusFlag('live');
        } else {
          setData(null);
          setStatusFlag('placeholder');
        }
      } catch (err) {
        if (active) { setData(null); setStatusFlag('placeholder'); }
      } finally {
        if (active) setFetching(false);
      }
    })();
    return () => { active = false; };
  }, [query, page]);

  useEffect(() => {
    const onDoc = (e) => { if (barRef.current && !barRef.current.contains(e.target)) setOpenMenu(''); };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const warming = statusFlag === 'live' && data?.warming;
  const live = statusFlag === 'live' && data && !warming;
  const rows = live ? data.members : SAMPLE_MEMBERS;
  const totals = live ? data.totals : SAMPLE_TOTALS;
  const cityGaps = live ? data.cityGaps || [] : SAMPLE_CITY_GAPS;
  // Full-page loader ONLY before the first payload has ever arrived.
  //
  // This used to key off `fetching` alone, which unmounted the whole subtree on
  // every refetch — and the search box lives inside that subtree. So each debounced
  // keystroke swapped the page for a full-height spinner and back, which flickered
  // AND stole focus from the input mid-word, making the box effectively unusable.
  // `statusFlag` leaves 'loading' on the first response and never returns to it, so
  // it is exactly the "nothing has arrived yet" test. Refetches now keep the page
  // mounted and just dim the results (see the .busy class on the table card).
  const firstLoad = fetching && statusFlag === 'loading';
  const total = live ? data.total : SAMPLE_MEMBERS.length;
  const pages = live ? data.pages : 1;
  // The radius that ACTUALLY produced these buckets, taken off the response. The
  // local constants are only the sample-mode fallback — the page must never label
  // itself with a distance the server did not use.
  const radius = (live ? data.radiusKm : SAMPLE_TOTALS.radiusKm) || BOT_REACH_KM;
  const radiusMi = (live ? data.botReachMiles : null) || BOT_REACH_MILES;
  // Memoised because the coverage bar below keys a useMemo off it: a fresh `{}`
  // fallback on every render would rebuild the bar on every render.
  const buckets = useMemo(() => totals?.buckets || {}, [totals]);
  const activeScope = SCOPES.find((s) => s.key === scope) || SCOPES[0];

  const countries = useMemo(
    () => [{ name: 'All', n: total }, ...(live ? data.countries || [] : [])],
    [live, data, total],
  );
  const cities = useMemo(
    () => [{ name: 'All', n: total }, ...(live ? data.cities || [] : [])],
    [live, data, total],
  );

  // Coverage bar: the four buckets as one 100% stack of the whole audience. Widths
  // come from the UNFILTERED bucket counts, so the bar always describes the
  // population — narrowing to one city must not redraw it as if the whole product
  // looked like that city.
  const barSegs = useMemo(() => {
    const denom = (buckets.covered || 0) + (buckets.nomessage || 0) + (buckets.norange || 0) + (buckets.nolocation || 0);
    if (!denom) return [];
    return [
      { k: 'covered', cls: 'seg-covered', label: 'Reached', n: buckets.covered || 0 },
      { k: 'nomessage', cls: 'seg-nomessage', label: 'Never messaged', n: buckets.nomessage || 0 },
      { k: 'norange', cls: 'seg-norange', label: 'No bot in range', n: buckets.norange || 0 },
      { k: 'nolocation', cls: 'seg-nolocation', label: 'No location', n: buckets.nolocation || 0 },
    ].map((s) => ({ ...s, pct: (s.n / denom) * 100 }));
  }, [buckets]);

  const maxCityN = cityGaps.length ? Math.max(...cityGaps.map((c) => c.n)) : 1;

  const toggleMenu = (id) => (e) => { e.stopPropagation(); setOpenMenu((m) => (m === id ? '' : id)); };
  const pick = (setter) => (val) => { setter(val); setOpenMenu(''); };
  // City options are scoped to the country server-side, so a city left over from the
  // previous country would be a filter pair that matches nobody.
  const pickCountry = (val) => { setCountry(val); setCity('All'); setOpenMenu(''); };

  const clearAll = () => {
    setGender('all'); setPlan('All'); setCountry('All'); setCity('All');
    setJoined('all'); setFrom(shiftDays(TODAY, -29)); setTo(TODAY);
    setSort('wait'); setQ(''); setAudience('active');
  };

  // Rows open the same UserProfileMainPage modal the Member Engagement and Channel
  // Attribution lists use, rather than navigating away — an admin working through a
  // filtered list should not lose it to look at one member.
  const [profileUserId, setProfileUserId] = useState(null);
  const openProfile = useCallback((id) => { if (id) setProfileUserId(id); }, []);
  const closeProfile = useCallback(() => setProfileUserId(null), []);

  // Export the whole filtered population, not just the page on screen. Deliberately
  // ONE request: paging an export lets a background snapshot refresh land mid-way,
  // shifting rows across page boundaries so members are duplicated or dropped.
  const [exporting, setExporting] = useState(false);
  const EXPORT_LIMIT = 50000;
  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = await getBotCoverage({ ...query, page: 1, limit: EXPORT_LIMIT });
      if (all?.warming) {
        window.alert('Bot coverage figures are still being computed after a server restart. Try again in a minute.');
        return;
      }
      if (all?.truncated) {
        window.alert(`This export would be incomplete: ${all.total.toLocaleString()} members match but only ${all.members.length.toLocaleString()} can be exported at once. Narrow the filters and try again.`);
        return;
      }
      const list = all?.members?.length ? all.members : rows;
      const head = ['Member', 'Member ID', 'Gender', 'Account', 'City', 'State', 'Country', 'Location source', 'Seeking', 'Nearest matching bot (km)', 'Nearest bot name', 'Nearest bot any gender (km)', 'Bot messages received', 'Plan', 'Paying now', 'INR/month', 'Joined (days ago)', 'Last active'];
      const lines = list.map((m) => [
        m.name, m.id, m.gender, m.account ? statusLabel(m.account) : '', m.city, m.state, m.country, m.originSrc || 'none',
        m.prefGender || 'any', m.nearestKm ?? '', m.nearestBot?.name ?? '', m.nearestAnyKm ?? '',
        m.botMsgs ?? 0, m.tier, m.paying ? 'yes' : 'no', m.monthlyINR ?? 0,
        m.joinedDaysAgo ?? '', m.lastActive || '',
      ]);
      const csv = [head, ...lines].map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bot-coverage-${scope}-${radiusMi}mi.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const pageList = useMemo(() => {
    const out = [];
    for (let i = 1; i <= pages; i += 1) {
      if (i === 1 || i === pages || Math.abs(i - page) <= 1) out.push(i);
      else if (out[out.length - 1] !== '…') out.push('…');
    }
    return out;
  }, [pages, page]);

  const audienceWord = audience === 'all' ? 'members' : 'active members';
  const pctOfAudience = totals?.audienceTotal
    ? Math.round(((buckets[activeScope.bucket] || 0) / totals.audienceTotal) * 1000) / 10
    : null;

  return (
    <Box sx={{ pb: 4 }}>
      <style>{styles}</style>

      <Stack direction="row" justifyContent={embedded ? 'flex-end' : 'space-between'} alignItems="flex-start" sx={{ mb: embedded ? 0 : 2 }}>
        {!embedded && (
          <HeadTitle
            imageSrc="/assets/images/admin-panel/analytics.png"
            text="Bot Coverage"
            description="Members no bot is reaching — no bot in range, never messaged, or no location at all"
          />
        )}
        {statusFlag === 'placeholder' && (
          <Chip size="small" color="warning" variant="outlined" label="Showing sample data — live source unavailable" />
        )}
      </Stack>

      <div className="favor-bc">
        {firstLoad ? (
          <div style={{ minHeight: 'calc(100vh - 240px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader />
          </div>
        ) : warming ? (
          <div className="warmbox">
            <b>Building the coverage snapshot…</b>
            Every member&rsquo;s nearest reachable bot is being computed against the whole bot roster.
            This runs once after a server restart and takes a minute or two — refresh shortly.
            <div style={{ marginTop: 8, fontSize: 12 }}>
              No numbers are shown until it finishes, deliberately: a partial scan would read as a coverage gap that isn&rsquo;t there.
            </div>
          </div>
        ) : (
          <>
            {/* Scope switcher. Each card carries its own population count so an admin
                can see the shape of the problem before choosing which one to work. */}
            <div className="scopebar">
              {SCOPES.map((s) => (
                <button key={s.key} type="button" className={`scope ${scope === s.key ? 'on' : ''}`} onClick={() => setScope(s.key)}>
                  <span className="bar" />
                  <div className="t">{s.label}</div>
                  <div className="n">{nfmt(buckets[s.bucket])}</div>
                  <div className="s">{s.hint}</div>
                </button>
              ))}
            </div>

            <div className="stats-ctx">
              {live
                ? `Snapshot of ${fmtClock(data.snapshotAt)} · ${nfmt(data.botRoster?.botsPlaced)} placeable bots (${nfmt(data.botRoster?.botGender?.Female)} female, ${nfmt(data.botRoster?.botGender?.Male)} male)`
                : 'Sample data — every figure below is invented'}
            </div>

            <section className="stats">
              <div className="stat"><span className="bar" />
                <div className="k">{activeScope.label}</div>
                <div className="v">{nfmt(totals.count)}</div>
                <div className="d">
                  {pctOfAudience != null
                    ? `${nfmt(buckets[activeScope.bucket])} of ${nfmt(totals.audienceTotal)} ${audienceWord} (${pctOfAudience}%) · showing ${nfmt(totals.count)} after filters`
                    : '—'}
                </div>
              </div>

              <div className="stat s2"><span className="bar" />
                <div className="k">By gender</div>
                <div className="split">
                  <div className="m"><b>{nfmt(totals.men)}</b><span>Men</span></div>
                  <div className="w"><b>{nfmt(totals.women)}</b><span>Women</span></div>
                </div>
                <div className="d">
                  {totals.count
                    ? `${Math.round((Math.max(totals.men, totals.women) / totals.count) * 100)}% are ${totals.men >= totals.women ? 'men' : 'women'}`
                    : 'no members in this selection'}
                </div>
              </div>

              <div className="stat s3"><span className="bar" />
                <div className="k">Avg wait since signup</div>
                <div className="v">{totals.avgWaitDays == null ? '—' : totals.avgWaitDays} <span className="unit">days</span></div>
                <div className="d">
                  {totals.longestWait
                    ? `Longest wait: ${totals.longestWait.days} days (${totals.longestWait.name}${totals.longestWait.city ? `, ${totals.longestWait.city}` : ''})`
                    : '—'}
                </div>
              </div>

              {/* Money is the tile that turns this page into a priority list, so it
                  counts ONLY currently-Active subscriptions: a lapsed plan is not
                  revenue at risk, it is revenue already gone. */}
              <div className="stat risk s4"><span className="bar" />
                <div className="k">Paying &amp; not reached</div>
                <div className="v">{nfmt(totals.paidCount)}</div>
                <div className="d">{inr(totals.paidMonthlyINR)}/mo in live subscriptions we are not reaching</div>
              </div>

              {scope === 'norange' ? (
                <div className="stat s5"><span className="bar" />
                  <div className="k">Biggest location gap</div>
                  <div className="v sm">{totals.biggestCityGap?.city || '—'}</div>
                  <div className="d">
                    {totals.biggestCityGap
                      ? `${totals.biggestCityGap.n} members · nearest bot ${fmtDist(totals.biggestCityGap.nearestKm)} away`
                      : 'no city gap in this selection'}
                  </div>
                </div>
              ) : (
                <div className="stat s5"><span className="bar" />
                  <div className="k">Median distance to a bot</div>
                  <div className="v sm">{fmtDist(totals.medianNearestKm)}</div>
                  <div className="d">half of these members are closer than this to a bot they could see</div>
                </div>
              )}
            </section>

            {/* Whole-audience coverage stack. Always the unfiltered population — it is
                the context every other number on the page is read against. */}
            {barSegs.length > 0 && (
              <div className="coverage">
                <div className="cap">
                  <h3>Coverage of all {nfmt(totals.audienceTotal)} {audienceWord} · {radiusMi} mi radius</h3>
                  <div className="legend">
                    {barSegs.map((s) => (
                      <span key={s.k}>
                        <span className="dot" style={{ background: { covered: '#1f9d6b', nomessage: '#b07d18', norange: '#cf4233', nolocation: '#9b978f' }[s.k] }} />
                        {s.label} {nfmt(s.n)}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="track">
                  {barSegs.filter((s) => s.pct > 0).map((s) => (
                    <div key={s.k} className={s.cls} style={{ width: `${s.pct}%` }} title={`${s.label}: ${nfmt(s.n)} (${s.pct.toFixed(1)}%)`}>
                      {s.pct >= 7 ? `${Math.round(s.pct)}%` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid2">
              <div className="panel">
                <h3>Cities in this selection</h3>
                <div className="phint">
                  Bar length = members here in the <b>{activeScope.label.toLowerCase()}</b> bucket ·{' '}
                  <span style={{ color: '#2f7df6', fontWeight: 600 }}>blue</span> men,{' '}
                  <span style={{ color: '#d6457f', fontWeight: 600 }}>rose</span> women. Click a city to filter the table.
                </div>
                {cityGaps.length === 0 ? (
                  <div className="emptypanel">No members in this selection.</div>
                ) : cityGaps.slice(0, 10).map((c) => {
                  const known = c.men + c.women;
                  return (
                    <div
                      key={`${c.city}|${c.state}|${c.country}`}
                      className={`locrow ${hasCity(c) ? '' : 'nofilter'} ${city === c.city ? 'on' : ''}`}
                      onClick={() => hasCity(c) && setCity(city === c.city ? 'All' : c.city)}
                      title={hasCity(c) ? 'Filter the table to this city' : 'These members have no city recorded, so they cannot be filtered by city'}
                    >
                      <div className="city">{placeLines(c).head}<small>{placeLines(c).rest}</small></div>
                      <div className="locbar">
                        <span className="gsplit" style={{ width: `${Math.max((c.n / maxCityN) * 100, 3)}%` }}>
                          {/* Split by the members whose gender is known, so an "Other"
                              row can't silently stretch one of the two colours. */}
                          <i className="gm" style={{ width: `${known ? (c.men / known) * 100 : 50}%` }} />
                          <i className="gw" style={{ width: `${known ? (c.women / known) * 100 : 50}%` }} />
                        </span>
                      </div>
                      <div className="nums">
                        <b>{c.n}</b> {c.n === 1 ? 'member' : 'members'}
                        {c.nearestKm != null ? ` · nearest bot ${fmtDist(c.nearestKm)}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="panel">
                <h3>By plan</h3>
                <div className="phint">A paying member we are not reaching is the most expensive kind of gap.</div>
                {['Luxury', 'Gold', 'Free'].map((t) => {
                  const row = totals.tiers?.[t] || { n: 0, inr: 0 };
                  return (
                    <div className="planrow" key={t}>
                      <div>{tierPill(t)}</div>
                      <div style={{ textAlign: 'right' }}>
                        <span className="n">{nfmt(row.n)}</span>
                        <span className="sub">{row.inr ? `· ${inr(row.inr)}/mo at risk` : t === 'Free' ? '· no revenue at risk' : '· no live subscriptions'}</span>
                      </div>
                    </div>
                  );
                })}
                <div className="foot-note">
                  {/* The bot roster is ~3:1 female. That is the single biggest reason a
                      city can look covered and still leave women uncovered, so it is
                      stated here rather than left for someone to work out. */}
                  {live && data.botRoster ? (
                    <>
                      The roster is <b>{nfmt(data.botRoster.botGender?.Female)}</b> female and{' '}
                      <b>{nfmt(data.botRoster.botGender?.Male)}</b> male bots. A member seeking men can only be
                      reached by a male bot, so the thinner side of the roster is where gaps concentrate.
                    </>
                  ) : (
                    'Members reached by a bot in their first days convert far better than those left waiting.'
                  )}
                </div>
              </div>
            </div>

            <div className="search">
              <span className="mag" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, city, country, member ID…" />
            </div>

            <div className="filterbar" ref={barRef}>
              <div className="fgroup">
                <span className="flabel">Audience</span>
                <div className="seg">
                  {AUDIENCES.map(([k, l]) => (
                    <button key={k} type="button" className={`opt ${audience === k ? 'on' : ''}`} onClick={() => setAudience(k)}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="divider" />
              <div className="fgroup">
                <span className="flabel">Gender</span>
                <div className="seg">
                  {[['all', 'All'], ['f', 'Female'], ['m', 'Male']].map(([k, l]) => (
                    <button key={k} type="button" className={`opt ${gender === k ? 'on' : ''}`} onClick={() => setGender(k)}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="dd">
                <div className={`pillfilter ${plan !== 'All' ? 'on' : ''}`} onClick={toggleMenu('plan')}>Plan: <b>{(PLANS.find(([k]) => k === plan) || PLANS[0])[1]}</b><span className="car" /></div>
                <div className={`ddmenu ${openMenu === 'plan' ? 'open' : ''}`}>
                  {PLANS.map(([k, l]) => (<div key={k} className={`item ${plan === k ? 'sel' : ''}`} onClick={() => pick(setPlan)(k)}>{l}</div>))}
                </div>
              </div>
              <div className="dd">
                <div className={`pillfilter ${country !== 'All' ? 'on' : ''}`} onClick={toggleMenu('country')}>Country: <b>{country}</b><span className="car" /></div>
                <div className={`ddmenu ${openMenu === 'country' ? 'open' : ''}`}>
                  {countries.map((c) => (
                    <div key={c.name} className={`item ${country === c.name ? 'sel' : ''}`} onClick={() => pickCountry(c.name)}>
                      {c.name}{c.n != null ? <span className="cnt">{c.n.toLocaleString()}</span> : null}
                    </div>
                  ))}
                </div>
              </div>
              <div className="dd">
                <div className={`pillfilter ${city !== 'All' ? 'on' : ''}`} onClick={toggleMenu('city')}>City: <b>{city}</b><span className="car" /></div>
                <div className={`ddmenu ${openMenu === 'city' ? 'open' : ''}`}>
                  {cities.map((c) => (
                    <div key={c.name} className={`item ${city === c.name ? 'sel' : ''}`} onClick={() => pick(setCity)(c.name)}>
                      {c.name}{c.n != null ? <span className="cnt">{c.n.toLocaleString()}</span> : null}
                    </div>
                  ))}
                </div>
              </div>
              <div className="dd">
                <div className={`pillfilter ${joined !== 'all' ? 'on' : ''}`} onClick={toggleMenu('joined')}>Joined: <b>{(JOINED.find(([k]) => k === joined) || JOINED[3])[1]}</b><span className="car" /></div>
                <div className={`ddmenu ${openMenu === 'joined' ? 'open' : ''}`}>
                  {JOINED.map(([k, l]) => (<div key={k} className={`item ${joined === k ? 'sel' : ''}`} onClick={() => pick(setJoined)(k)}>{l}</div>))}
                </div>
              </div>
              {joined === 'custom' && (
                <span className="daterange">
                  <input type="date" max={TODAY} value={from} onChange={(e) => { const v = e.target.value; setFrom(v); if (v > to) setTo(v); }} />
                  <span className="dsep">to</span>
                  <input type="date" max={TODAY} value={to} onChange={(e) => { const v = e.target.value; setTo(v); if (v < from) setFrom(v); }} />
                </span>
              )}
              <div className="dd">
                <div className="pillfilter" onClick={toggleMenu('sort')}>Sort: <b>{SORTS[sort]}</b><span className="car" /></div>
                <div className={`ddmenu ${openMenu === 'sort' ? 'open' : ''}`}>
                  {Object.entries(SORTS).map(([k, l]) => (<div key={k} className={`item ${sort === k ? 'sel' : ''}`} onClick={() => pick(setSort)(k)}>{l}</div>))}
                </div>
              </div>
              <span className="clearf" onClick={clearAll}>Clear all</span>
              <button type="button" className="export-btn" onClick={exportCsv} disabled={exporting}><span>⤓</span> {exporting ? 'Exporting…' : 'Export CSV'}</button>
            </div>

            <div className={`tablecard stickyhead ${fetching ? 'busy' : ''}`}>
              <table>
                <colgroup>
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '12%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Gender</th>
                    <th>Location</th>
                    <th
                      className="num sortable"
                      onClick={() => setSort('distance')}
                      title="Distance to the nearest bot whose gender matches what this member is seeking. The small line is the nearest bot of ANY gender, when that is closer."
                    >
                      Nearest bot {sort === 'distance' && <span className="sortc">▾</span>}
                    </th>
                    <th className="sortable" onClick={() => setSort('paid')} title="Latest subscription in any subscriber state. The price line shows the monthly run-rate of a currently-active plan only.">
                      Plan {sort === 'paid' && <span className="sortc">▾</span>}
                    </th>
                    <th className="num" title="How many messages this member has ever received from a bot. All-time — the Joined filter selects which members are listed, it does not window this count.">Bot msgs</th>
                    <th className="num sortable" onClick={() => setSort('active')} title="Last time this member was seen.">
                      Last active {sort === 'active' && <span className="sortc">▾</span>}
                    </th>
                    <th className="num sortable" onClick={() => setSort('wait')} title="Days since signup — how long this member has been waiting to be reached.">
                      Waiting {sort === 'wait' && <span className="sortc">▾</span>}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr className="emptyrow"><td colSpan={8}>No members match these filters.</td></tr>
                  ) : rows.map((m, i) => (
                    <tr key={m.id} className={i % 2 ? 'alt' : ''} onClick={() => openProfile(m.id)}>
                      <td>
                        <div className="member">
                          <span className="ava">
                            {m.profilePicture ? <img src={m.profilePicture} alt="" /> : m.initials}
                            {m.verified && <span className="ava-badge" />}
                          </span>
                          <div className="m-meta">
                            <div className="nm">{m.name} {acctPill(m.account)}</div>
                            <div className="sub">
                              {m.profileCompletion != null ? `Profile ${m.profileCompletion}% complete` : 'Profile —'}
                              {/* `prefInferred` means they never stated a preference and the bot
                                  sender infers the opposite of their own gender — same rule this
                                  row was scored by. Saying "seeking male" flat would present an
                                  inference as something the member actually asked for. */}
                              {m.prefGender
                                ? ` · ${m.prefInferred ? 'assumed seeking' : 'seeking'} ${m.prefGender.toLowerCase()}`
                                : ' · no gender preference'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{genderChip(m.gender)}</td>
                      <td className="city-cell">
                        {placeLines(m).head}
                        <small>
                          {placeLines(m).rest}
                          {/* Where the coordinates came from. A profile point is the
                              location the admin sees on the member's card; a live point
                              is the last one their client reported, and the two drift
                              (277 km in ADMIN-10), so which one placed this row matters. */}
                          {m.originSrc === 'live' ? `${placeLines(m).rest ? ' · ' : ''}live location` : ''}
                        </small>
                      </td>
                      <td className="num">
                        {m.originSrc == null ? (
                          <span className="pill neutral">No location</span>
                        ) : m.nearestKm == null ? (
                          <span className="pill bad">No bot found</span>
                        ) : (
                          <div className="nearest">
                            <span className={`pill ${distClass(m.nearestKm, radius)}`}>{fmtDist(m.nearestKm)}</span>
                            <small>
                              {m.nearestBot?.name ? `${m.nearestBot.name}${m.nearestBot.city ? `, ${m.nearestBot.city}` : ''}` : '—'}
                            </small>
                            {/* Says the gap is a ROSTER-MIX problem, not a placement
                                one: there is a bot within the radius, it is just the
                                wrong gender for this member. */}
                            {genderBlocked(m, radius) && (
                              <small style={{ color: '#b07d18' }}>
                                bot {fmtDist(m.nearestAnyKm)} away, wrong gender
                              </small>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {tierPill(m.tier)}
                        <span className="plan-price">
                          {m.paying ? `${inr(m.monthlyINR)}/mo` : m.tierLapsed ? 'lapsed' : '—'}
                        </span>
                      </td>
                      <td className="num"><div className="nearest">{m.botMsgs ?? 0}</div></td>
                      <td className="num" style={{ fontSize: 12.5, color: '#6d6a64' }}>{relTime(m.lastActive)}</td>
                      <td className="num">
                        <div className={`days ${waitClass(m.joinedDaysAgo ?? 0)}`}>
                          {m.joinedDaysAgo == null ? '—' : `${m.joinedDaysAgo}d`}
                          <small>{joinedLabel(m.joinedDaysAgo)}</small>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="foot">
                <div className="info">
                  Showing <b>{rows.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{(page - 1) * PAGE_SIZE + rows.length}</b> of{' '}
                  <b>{nfmt(total)}</b> {audienceWord} · {activeScope.label.toLowerCase()} · {radiusMi} mi radius
                </div>
                {pages > 1 && (
                  <div className="pager">
                    <span className={`pg ${page === 1 ? 'off' : ''}`} onClick={() => page > 1 && setPage(page - 1)}>‹</span>
                    {pageList.map((p, i) => (p === '…'
                      ? <span className="pg dots" key={`d${i}`}>…</span>
                      : <span key={p} className={`pg ${p === page ? 'cur' : ''}`} onClick={() => setPage(p)}>{p}</span>))}
                    <span className={`pg ${page === pages ? 'off' : ''}`} onClick={() => page < pages && setPage(page + 1)}>›</span>
                  </div>
                )}
              </div>
            </div>

            <div className="note">
              Nearest bot is measured from the member&rsquo;s profile location, falling back to their last reported live
              location, against every non-suspended bot&rsquo;s location — the same origin precedence the Initiate Chat
              picker uses. Bot messages come from the live chat store. Figures are a
              cached snapshot, rebuilt every 15 minutes.
            </div>
          </>
        )}
      </div>

      {/* UserProfileMainPage fetches once on mount, so it is KEYED on the id —
          a reused instance would show the previously opened member. */}
      <Modal open={!!profileUserId} onClose={closeProfile} aria-labelledby="bot-coverage-profile-modal" sx={{ zIndex: 1500 }}>
        <Box sx={PROFILE_MODAL_SX}>
          <Box sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10, padding: '10px' }}>
            <IconButton sx={{ position: 'absolute', top: 10, right: 0 }} onClick={closeProfile}>
              <CloseIcon />
            </IconButton>
          </Box>
          {profileUserId && <UserProfileMainPage key={profileUserId} userId={profileUserId} />}
        </Box>
      </Modal>
    </Box>
  );
};

export default BotCoverage;
